import React, { useEffect, useRef, useState } from "react"
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import CloseIcon from "../../images/CloseIcon";
import { deleteSalePoint, getListSalePointForDashBoard, updateSalePoint } from "../../services/salePoint.service";
import { showDeleteToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import PlusIconWhite from "../../images/PlusIconWhite";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import { useResponsive } from "../../hook/useResponsive";
import SalePointIcon from "../../images/SalePointIcon";
import {
    ListColumn,
    ListFilter,
    ListQuery,
    ListRowAction,
    ListScreen,
    ListScreenHandle,
    cells,
} from '../../components/list';

interface SalePointRow {
    id: string;
    salePointName: string;
    description: string | null;
    position: number | null;
    pricePointId: string | null;
    isActive: boolean;
}
interface SalePointResponse {
    data: {
        data: {
            salePoints: SalePointRow[];
            count: number
        }
    }

}
const SalePoint = () => {
    const preLanguage = localStorage.getItem("language");
    const { isMobile } = useResponsive();
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<ListScreenHandle>(null);
    const [language, setLanguage] = useState<any>(null);
    const companyId: string = localStorage.getItem("companyId") || "";
    const [showDelete, setShowDelete] = useState<boolean>(false);
    const [idDelete, setIdDelete] = useState<string>("")
    const inforTable: any = {
        img: <SalePointIcon />, title: language && language?.["No sale points yet"] ? language?.["No sale points yet"] : "No sale points yet", path: "/salePoint/new", desc: language && language?.["Create and manage Sale Points like Restaurant, Bar, or Terrace to enable location-specific menus, pricing, and revenue reports."] ? language?.["Create and manage Sale Points like Restaurant, Bar, or Terrace to enable location-specific menus, pricing, and revenue reports."] : "Create and manage Sale Points like Restaurant, Bar, or Terrace to enable location-specific menus, pricing, and revenue reports.", titleCreate: language && language?.["New Sale Points"] ? language?.["New Sale Points"] : "New Sale Points"
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
            event.preventDefault();
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };

    }, []);
    useEffect(() => {
        if (window.languageContext) {
            setLanguage(window.languageContext.language);
        }
    }, [window, window.languageContext]);

    /**
     * One page of sale points. The search box maps onto `searchName`; the
     * Description / Price Point / Position text filters onto the endpoint's
     * other per-column params.
     */
    const fetchSalePoints = async (query: ListQuery) => {
        webLoadingFalse();
        const response: SalePointResponse = await getListSalePointForDashBoard(
            String(query.page),
            query.limit,
            query.search,
            query.filters.searchDescription,
            query.filters.searchPricePoint,
            query.filters.searchPosition
        );
        return {
            rows: response.data.data.salePoints ?? [],
            count: response.data.data.count ?? 0,
        };
    };

    const goToSalePoint = (id: string) => webNavigate('/' + preLanguage + '/salePoint/' + id);

    const handleShowDelete = (id: string) => {
        setShowDelete(true);
        setIdDelete(id)
    }
    const handleDeleteEmployee = async () => {
        setShowDelete(false);
        try {
            const response = await deleteSalePoint(idDelete, companyId);
            showDeleteToast(language?.['Sale Point Deleted !'] ?? 'Sale Point Deleted !');
        } catch (error: any) {
            console.error("Error loading terms:", error);
            showApiError(error, { language });
        } finally {
            listRef.current?.reload();
        }
    }

    const handleUpdateStatus = async (row: SalePointRow) => {
        try {
            // Same payload as before: the whole row with isActive flipped.
            await updateSalePoint(row.id, companyId, {
                ...row,
                isActive: !row.isActive
            } as any);
        } catch (error) {
            console.error("Error loading terms:", error);
            showApiError(error, { language });
        } finally {
            listRef.current?.reload();
        }
    }

    /* ---------------- List kit configuration ---------------- */

    const columns: ListColumn<SalePointRow>[] = [
        {
            key: 'salePointName',
            label: 'Sale Point',
            width: 240,
            minWidth: 120,
            hideable: false,
            render: cells.primary(s => s.salePointName),
            mobile: 'title',
            onClick: s => goToSalePoint(String(s.id)),
        },
        {
            key: 'description',
            label: 'Description',
            width: 220,
            minWidth: 120,
            value: s => s.description ?? '/',
            mobile: 'subtitle',
        },
        {
            key: 'pricePointId',
            label: 'Price Point',
            width: 220,
            minWidth: 100,
            value: s => (s.pricePointId !== null && s.pricePointId !== undefined ? s.pricePointId : '/'),
            mobile: 'sheet',
        },
        {
            key: 'position',
            label: 'Position',
            width: 90,
            align: 'right',
            value: s => s.position ?? '',
            mobile: 'sheet',
        },
        {
            key: 'isActive',
            label: 'Status',
            width: 88,
            render: cells.toggle(s => !!s.isActive, handleUpdateStatus),
            mobile: 'trailing',
        },
    ];

    const filters: ListFilter[] = [
        { key: 'searchDescription', label: 'Description', kind: 'text' },
        { key: 'searchPricePoint', label: 'Price Point', kind: 'text' },
        { key: 'searchPosition', label: 'Position', kind: 'text' },
    ];

    const rowActions: ListRowAction<SalePointRow>[] = [
        {
            key: 'edit',
            label: 'Edit',
            icon: <EditIconV2 />,
            sheetIcon: 'inventory',
            onRun: s => goToSalePoint(String(s.id)),
        },
        {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteIconV2 />,
            sheetIcon: 'close',
            danger: true,
            onRun: s => handleShowDelete(String(s.id)),
        },
    ];

    return (
        <>
            <div
                className="h-full relative"
                style={{
                    overflowY: isMobile ? "auto" : undefined,
                    paddingBottom: isMobile ? "24px" : undefined,
                    display: isMobile ? undefined : 'flex',
                    flexDirection: isMobile ? undefined : 'column',
                }}
            >
                <div className="">
                    <div className="lg:flex items-center justify-between" >
                        <p
                            style={{ color: '#222525' }}
                            className="text-[24px] leading-[130%] font-medium font-inter"
                        >
                            {language && language?.['Sale points']
                                ? language?.['Sale points']
                                : 'Sale points'}
                        </p>
                        <div
                            className="cursor-pointer flex items-center justify-center"
                            style={{
                                gap: '8px',
                                padding: "8px 24px 8px 16px",
                                backgroundColor: '#0F7679',
                                borderRadius: '30px',
                                width: "fit-content", marginTop: isMobile ? "20px" : '0px',
                            }}
                            onClick={() => {
                                webNavigate('/' + preLanguage + '/salePoint/new');
                            }}
                        >
                            <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
                                <PlusIconWhite />
                            </div>
                            <p
                                style={{ color: '#FFFFFF' }}
                                className="text-[14px] leading-[130%] font-medium font-inter"
                            >
                                {language && language?.['Add sale point']
                                    ? language?.['Add sale point']
                                    : 'Add sale point'}
                            </p>
                        </div>
                    </div>
                </div>
                <ListScreen
                    ref={listRef}
                    language={language}
                    columns={columns}
                    fetchRows={fetchSalePoints}
                    searchPlaceholder="Search sale points"
                    searchInputRef={searchInputRef}
                    filters={filters}
                    rowActions={rowActions}
                    countNoun="sale points"
                    emptyState={inforTable}
                />

            </div>
            <div
                className={`fixed inset-0 flex items-center justify-center px-4 ${showDelete ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} transition-opacity duration-300 ease-in-out`}
                style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100vw", height: "100vh", left: 0, top: 0 }}
            >
                <div
                    className={`transform transition-transform duration-300 ease-in-out ${showDelete ? "translate-y-0" : "translate-y-4 opacity-0"}`} style={{
                        padding: "24px", borderRadius: "24px", backgroundColor: "#FFFFFF", width: "530px", maxWidth: "calc(100vw - 32px)", transition: "transform 0.3s ease, opacity 0.3s ease",
                    }}
                >
                    <div className="flex items-center justify-between">
                        <p
                            style={{ fontSize: '20px', color: '#222525' }}
                            className="font-semibold font-inter leading-[24px]"
                        >
                            {language && language?.['Delete sale point?']
                                ? language?.['Delete sale point?']
                                : 'Delete sale point?'}
                        </p>
                        <div
                            onClick={() => setShowDelete(false)}
                            className="flex items-center justify-center cursor-pointer"
                            style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '100%',
                            }}
                        >
                            <CloseIcon />
                        </div>
                    </div>
                    <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
                        <p className="text-[14px] font-inter leading-[24px]" style={{ color: '#6A7878' }}>
                            {language &&
                                language?.[
                                'This sale point will be permanently deleted. Are you sure you want to continue'
                                ]
                                ? language?.[
                                'This sale point will be permanently deleted. Are you sure you want to continue'
                                ]
                                : 'This sale point will be permanently deleted. Are you sure you want to continue'}
                        </p>
                        {/* <span className="text-main">[sale ID]</span> sale. */}
                    </div>
                    <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
                        <div
                            onClick={() => setShowDelete(false)}
                            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                            style={{
                                borderRadius: '30px',
                                padding: '10px 24px',
                                border: '1px solid #C9D7D8',
                                color: '#222525',
                            }}
                        >
                            {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
                        </div>
                        <div
                            onClick={handleDeleteEmployee}
                            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                            style={{
                                borderRadius: '30px',
                                padding: '10px 24px',
                                backgroundColor: '#BA0E13',
                                color: '#FFFFFF',
                            }}
                        >
                            {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
};

export default SalePoint;